/* Bulk agreement and optional protection. Shopify owns prices, discounts and payment. */
(() => {
  if (customElements.get('pj-bulk-cart')) return;
  const VERSION = 'bulk-2026-09-06-v1';
  const QUOTE_URL = 'https://plainjane-bulk-protection.kallum.workers.dev/quote';
  const root = window.Shopify?.routes?.root || '/';
  const panels = () => [...document.querySelectorAll('pj-bulk-cart')];
  const acceptedVersion = () => VERSION;
  const unacceptedVersion = () => '';
  let state = {agreed: false, protection: false, fingerprint: '', acceptedAt: ''};
  let busy = false, externalUpdate = false, ready = false, error = '';
  let queue = Promise.resolve();
  const isFee = item => item.product_type === 'Bulk Theft Protection' || /^PJ-BTP-\d+$/.test(item.sku || '');
  const isBulk = item => item.product_type === 'Bulk THCA Flower';
  const net = item => Number.isSafeInteger(item.final_line_price) ? item.final_line_price : item.original_line_price;
  function quote(cart) {
    const bulk = cart.items.filter(isBulk);
    const subtotal = bulk.reduce((n, item) => n + net(item), 0);
    return {bulk, subtotal, fee: Math.floor((subtotal + 20) / 40),
      fingerprint: `${cart.currency}|${bulk.map(item => `${item.variant_id}:${item.quantity}:${net(item)}`).sort().join(';')}`};
  }
  async function request(path, data) {
    const response = await fetch(root + path, {method: data ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: {'Accept': 'application/json', ...(data ? {'Content-Type': 'application/json'} : {})},
      ...(data ? {body: JSON.stringify(data)} : {})});
    const body = await response.json();
    if (!response.ok) throw new Error(body.description || 'Your cart could not be updated. Please try again.');
    return body;
  }
  const readCart = () => request('cart.js');
  const wait = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds));
  async function waitForProtection(quote) {
    for (let attempt = 0; attempt < 30; attempt++) {
      try {
        const response = await fetch(`${root}products/${encodeURIComponent(quote.handle)}.js?pj_quote=${Date.now()}`, {
          credentials: 'same-origin', cache: 'no-store', headers: {'Accept': 'application/json'}
        });
        if (response.ok) {
          const product = await response.json();
          const variant = product.variants?.find(item => item.id === quote.variantId);
          if (variant?.available && variant.price === quote.cents) return;
        }
      } catch (_) { /* Storefront propagation is retried below. */ }
      await wait(750);
    }
    throw new Error('Protection is taking longer than expected to prepare. Please try again.');
  }
  async function addProtection(variantId) {
    for (let attempt = 0; attempt < 30; attempt++) {
      try {
        return await request('cart/add.js', {items:[{id:variantId,quantity:1,properties:{_pj_bulk_protection:VERSION}}]});
      } catch (failure) {
        const transient = /sold out|not available/i.test(failure.message || '');
        if (!transient || attempt === 29) throw failure;
        await wait(750);
      }
    }
  }
  async function setProtection(cart, cents) {
    const old = cart.items.filter(isFee);
    if (old.length === 1 && old[0].quantity === 1 && old[0].original_line_price === cents) return cart;
    let quote;
    if (cents) {
      const response = await fetch(QUOTE_URL, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({cents,currency:cart.currency})});
      quote = await response.json();
      if (!response.ok || quote.cents !== cents || !Number.isSafeInteger(quote.variantId)) throw new Error(quote.error || 'Protection could not be quoted. Please try again.');
    }
    if (old.length) await request('cart/update.js', {updates:Object.fromEntries(old.map(item => [item.key,0]))});
    if (quote) {
      await waitForProtection(quote);
      await addProtection(quote.variantId);
    }
    return readCart();
  }
  function sectionIds() {
    return [...new Set(['cart-button', ...[...document.querySelectorAll('cart-section,cart-drawer')]
      .map(el => el.closest('.shopify-section')?.id.replace(/^shopify-section-/, '')).filter(Boolean)])].slice(0, 5);
  }
  async function refresh() {
    const response = await request('cart/update.js', {sections: sectionIds(), sections_url: location.pathname + location.search});
    document.dispatchEvent(new CustomEvent('cart:updated', {detail: {type: 'update', request: {}, response, pjProtection: true}}));
    return response;
  }
  function render() {
    const active = panels();
    document.documentElement.classList.toggle('pj-bulk-cart-present', active.length > 0);
    for (const panel of active) {
      const agree = panel.querySelector('[data-bulk-agree]');
      const protect = panel.querySelector('[data-bulk-protect]');
      agree.checked = state.agreed;
      protect.checked = state.protection;
      agree.disabled = protect.disabled = busy || externalUpdate || !ready;
      panel.querySelector('[data-bulk-quote]').hidden = !state.protection;
      panel.querySelector('[data-bulk-status]').textContent = error || (busy || externalUpdate || !ready
        ? 'Updating your bulk order…' : state.agreed ? 'Bulk policy accepted. You can continue to checkout.' : 'Please agree to the bulk policy before checkout.');
    }
    for (const button of document.querySelectorAll('button[name="checkout"]')) {
      if (active.length) {
        button.dataset.pjBulkControlled = 'true';
        button.disabled = !ready || busy || externalUpdate || !state.agreed || Boolean(error);
      } else if (button.dataset.pjBulkControlled) {
        button.disabled = busy || externalUpdate || Boolean(error);
        if (!busy && !externalUpdate && !error) delete button.dataset.pjBulkControlled;
      }
    }
  }
  async function sync(action) {
    busy = true; error = ''; render();
    try {
      let cart = await readCart();
      let q = quote(cart);
      if (!ready) {
        const attributes = cart.attributes || {};
        if (attributes.pj_bulk_fingerprint === q.fingerprint) {
          const agreed = attributes.pj_bulk_policy_version === acceptedVersion();
          state = {agreed, protection: attributes.pj_bulk_protection === 'selected', fingerprint: q.fingerprint,
            acceptedAt: agreed ? (attributes.pj_bulk_policy_accepted_at || '') : ''};
        }
      }
      if (state.fingerprint !== q.fingerprint) state = {agreed: false, protection: false, fingerprint: q.fingerprint, acceptedAt: ''};
      if (action?.kind === 'agree') {state.agreed = action.value; state.acceptedAt = action.value ? new Date().toISOString() : '';}
      if (action?.kind === 'protect') state.protection = action.value;
      if (!q.bulk.length) state = {agreed: false, protection: false, fingerprint: q.fingerprint, acceptedAt: ''};
      if (state.protection && cart.currency !== 'USD') throw new Error('Protection is currently available in USD. Deselect protection to continue.');
      if (!q.bulk.length && !cart.items.some(isFee) && !cart.attributes?.pj_bulk_policy_version) {ready = true; return cart;}
      await request('cart/update.js', {attributes: {pj_bulk_policy_version: unacceptedVersion(), pj_bulk_protection: ''}});
      const desired = state.protection ? q.fee : 0;
      cart = await setProtection(cart, desired);
      q = quote(cart);
      // An order-level discount can also discount the service. Adjust against Shopify's returned amounts.
      for (let attempt = 0; state.protection && attempt < 4; attempt++) {
        const line = cart.items.find(isFee), paid = line ? net(line) : 0;
        if (paid === q.fee) break;
        if (!line || paid <= 0) throw new Error('Protection could not be priced with this discount. Deselect protection to continue.');
        const gross = Math.max(1, line.original_line_price + Math.round((q.fee - paid) * line.original_line_price / paid));
        cart = await setProtection(cart, gross);
        q = quote(cart);
      }
      const paid = cart.items.filter(isFee).reduce((n, item) => n + net(item), 0);
      if ((state.protection && paid !== q.fee) || (!state.protection && paid !== 0)) throw new Error('The protection amount could not be verified. Please deselect protection or try again.');
      state.fingerprint = q.fingerprint;
      await request('cart/update.js', {attributes: {
        pj_bulk_qa: '',
        pj_bulk_policy_version: state.agreed ? acceptedVersion() : unacceptedVersion(),
        pj_bulk_policy_accepted_at: state.agreed ? state.acceptedAt : '',
        pj_bulk_fingerprint: q.bulk.length ? q.fingerprint : '',
        pj_bulk_protection: q.bulk.length ? (state.protection ? 'selected' : 'declined') : '',
        pj_bulk_protection_fee_cents: state.protection ? String(paid) : '',
        pj_bulk_subtotal_cents: q.bulk.length ? String(q.subtotal) : ''
      }});
      await refresh();
      ready = true;
      return cart;
    } catch (failure) {
      ready = true; error = failure.message || 'Your cart could not be updated. Please try again.';
      throw failure;
    } finally {busy = false; render();}
  }
  function enqueue(action) {
    const next = queue.catch(() => {}).then(() => sync(action));
    queue = next;
    next.catch(() => {});
    return next;
  }
  class BulkCart extends HTMLElement {
    connectedCallback() { queueMicrotask(render); }
    disconnectedCallback() { queueMicrotask(render); }
  }
  customElements.define('pj-bulk-cart', BulkCart);
  document.addEventListener('change', event => {
    if (event.target.matches('[data-bulk-agree]')) enqueue({kind: 'agree', value: event.target.checked});
    if (event.target.matches('[data-bulk-protect]')) enqueue({kind: 'protect', value: event.target.checked});
  });
  async function checkout(event) {
    const button = event.target.closest?.('button[name="checkout"],a[href*="/checkout"]');
    const form = event.type === 'submit' && (event.submitter?.name === 'checkout' || event.target.querySelector('[name="checkout"]'));
    if ((!button && !form) || !panels().length) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (busy || externalUpdate || !state.agreed) return;
    try {
      const cart = await enqueue();
      if (state.agreed && !error && quote(cart).bulk.length) location.assign(root + 'checkout');
    } catch (_) { panels()[0]?.querySelector('[data-bulk-status]')?.focus(); }
  }
  document.addEventListener('click', checkout, true);
  document.addEventListener('submit', checkout, true);
  document.addEventListener('cart:updating', event => { if (!event.detail?.pjProtection) { externalUpdate = true; render(); } });
  document.addEventListener('cart:updated', event => {
    if (event.detail?.pjProtection) return;
    externalUpdate = false;
    if (panels().length || state.protection || state.agreed) enqueue();
    else render();
  });
  window.addEventListener('pageshow', event => { if (event.persisted) enqueue(); });
  const observer = new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 &&
      (node.matches?.('button[name="checkout"],pj-bulk-cart') || node.querySelector?.('button[name="checkout"],pj-bulk-cart'))))) render();
  });
  observer.observe(document.body, {childList: true, subtree: true});
  enqueue();
})();
